/**
 * Utility functions for Calendar module
 */

import { format, parse, startOfWeek, getDay } from 'date-fns';
import { dateFnsLocalizer } from 'react-big-calendar';
import { CalendarEvent } from '../../lib/api';
import { STATUS_COLORS, EVENT_TYPE_BG_COLORS } from './constants';
import { EventsByMonth } from './types';

const locales = {
  'en-US': require('date-fns/locale/en-US'),
};

export const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

export function getStatusBadgeClass(status: string): string {
  return STATUS_COLORS[status] || 'bg-gray-100 text-gray-800';
}

export function getEventStyleGetter(event: { resource?: unknown }) {
  const resource = event.resource as { event_type?: string } | undefined;
  const backgroundColor = EVENT_TYPE_BG_COLORS[resource?.event_type || ''] || '#6b7280';
  return {
    style: {
      backgroundColor,
      borderRadius: '4px',
      opacity: 0.8,
      color: 'white',
      border: '0px',
      display: 'block',
    },
  };
}

export function groupEventsByMonth(events: CalendarEvent[]): EventsByMonth {
  const groups: EventsByMonth = {};

  events.forEach((event) => {
    const monthKey = format(new Date(event.start_datetime), 'MMMM yyyy');
    if (!groups[monthKey]) {
      groups[monthKey] = [];
    }
    groups[monthKey].push(event);
  });

  // Sort events within each month by start date
  Object.keys(groups).forEach((key) => {
    groups[key].sort(
      (a, b) => new Date(a.start_datetime).getTime() - new Date(b.start_datetime).getTime()
    );
  });

  return groups;
}

export function filterEvents(
  events: CalendarEvent[],
  activeEventTypes: string[],
  selectedCustomer: string,
  eventPeriod: string
): CalendarEvent[] {
  return events.filter((event) => {
    const typeMatch = activeEventTypes.includes(event.event_type);
    const customerMatch =
      selectedCustomer === 'all' || event.company === parseInt(selectedCustomer);

    // Period filtering
    const now = new Date();
    const eventStart = new Date(event.start_datetime);
    const threeMonthsAgo = new Date(now);
    threeMonthsAgo.setMonth(now.getMonth() - 3);
    const sixMonthsAgo = new Date(now);
    sixMonthsAgo.setMonth(now.getMonth() - 6);

    let periodMatch = true;
    if (eventPeriod === 'upcoming') {
      periodMatch = new Date(event.end_datetime) >= now;
    } else if (eventPeriod === 'last_3_months') {
      periodMatch = eventStart >= threeMonthsAgo;
    } else if (eventPeriod === 'last_6_months') {
      periodMatch = eventStart >= sixMonthsAgo;
    }

    return typeMatch && customerMatch && periodMatch;
  });
}

export function transformEventsForCalendar(events: CalendarEvent[]) {
  return events.map((event) => ({
    id: event.id,
    title: event.title,
    start: new Date(event.start_datetime),
    end: new Date(event.end_datetime),
    resource: event,
    allDay: event.all_day,
  }));
}
